import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog } from "primereact/dialog";
import FaltaService from "../../services/resource/FaltaService";
import { CustomTable } from "../../componentes/table/CustomTable";
import Button from "../../componentes/button/button";
import FilialService from "../../services/resource/filialService";
import DropDown from "../../componentes/dropdown/dropdown";
import Input from "../../componentes/input/input";
import DateMasked from "../../componentes/inputMask/DateMasked";
import Textarea from "../../componentes/input/textarea";
import FuncionarioService from "../../services/resource/funcionarioService";
import FaltasImport from "./FaltasImport";
import MultiDatePicker from "../../componentes/inputMask/MultiDatePicker";

// ── Utilities ────────────────────────────────────────────────────────────────

function toInputDate(date) {
    return date.toISOString().split("T")[0];
}

function firstDayOfMonth(date = new Date()) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

function lastDayOfMonth(date = new Date()) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function fmtDate(iso) {
    if (!iso) return "—";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, icon, colorCls, delay }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay }}
            className={`group relative flex items-center gap-4 overflow-hidden rounded-xl border px-5 py-4 shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${colorCls}`}
        >
            <div className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full bg-current opacity-[0.06] transition-transform duration-300 group-hover:scale-125" />
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-card/70 text-lg shadow-xs ring-1 ring-current/10">
                <i className={icon} />
            </div>
            <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest opacity-60">{label}</p>
                <p className="text-3xl font-bold leading-tight tabular-nums">{value}</p>
            </div>
        </motion.div>
    );
}

function InfoRow({ label, value }) {
    return (
        <div className="flex items-center justify-between gap-4 border-b border-dashed border-border py-2.5 last:border-0">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
            <span className="text-sm font-semibold text-foreground text-right">{value || "—"}</span>
        </div>
    );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function Faltas() {
    const now = new Date();
    const faltaService = new FaltaService();
    const filialService = new FilialService();
    const funcionarioService = new FuncionarioService();

    const [faltas, setFaltas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inicio, setInicio] = useState(toInputDate(firstDayOfMonth(now)));
    const [fim, setFim] = useState(toInputDate(lastDayOfMonth(now)));
    const [busca, setBusca] = useState("");
    const [selected, setSelected] = useState(null);
    const [modalView, setModalView] = useState(false);
    const [modalForm, setModalForm] = useState(false);
    const [modalImport, setModalImport] = useState(false);
    const [modalFortes, setModalFortes] = useState(false);
    const [fortesFilialId, setFortesFilialId] = useState("");
    const [fortesLoading, setFortesLoading] = useState(false);
    const [fortesErro, setFortesErro] = useState("");
    const [fortesResultado, setFortesResultado] = useState(null);
    const [editando, setEditando] = useState(null);

    // form state
    const [form, setForm] = useState({
        funcionarioId: "",
        data: toInputDate(now),
        motivo: "",
        datas: []
    });
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState("");
    const [filiais, setFiliais] = useState([]);
    const [funcionarios, setFuncionarios] = useState([]);

    const carregar = () => {
        setLoading(true);
        faltaService.listar({ inicio, fim })
            .then(res => setFaltas(res.data))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        filialService.listar().then(res => {
            setFiliais(res.data);
        })
    }, [])

    useEffect(() => {
        funcionarioService.listar({ filialId: form.filialId }).then(res => {
            setFuncionarios(res.data)
        });

    }, [form.filialId])

    useEffect(() => {
        carregar();

    }, [inicio, fim]);

    // ── Stats ─────────────────────────────────────────────────────────────────

    const stats = useMemo(() => {
        const dias = (f) => f.quantidadeDias || 1;
        const total = faltas.reduce((soma, f) => soma + dias(f), 0);
        const funcionarios = new Set(faltas.map(f => f.funcionarioId)).size;
        const semMotivo = faltas.filter(f => !f.motivo).length;

        // most absent employee
        const contagem = {};
        faltas.forEach(f => { contagem[f.funcionarioNome] = (contagem[f.funcionarioNome] || 0) + dias(f); });
        const top = Object.entries(contagem).sort((a, b) => b[1] - a[1])[0];

        return { total, funcionarios, semMotivo, top };
    }, [faltas]);

    // ── Filtered data ─────────────────────────────────────────────────────────

    const filtered = useMemo(() => {
        if (!busca.trim()) return faltas;
        const q = busca.toLowerCase();
        return faltas.filter(f =>
            f.funcionarioNome?.toLowerCase().includes(q) ||
            f.funcionarioCodigo?.toLowerCase().includes(q) ||
            f.funcionarioCargo?.toLowerCase().includes(q) ||
            f.motivo?.toLowerCase().includes(q)
        );
    }, [faltas, busca]);

    // ── Handlers ─────────────────────────────────────────────────────────────

    const abrirCriar = () => {
        setEditando(null);
        setForm({ filialId: "", funcionarioId: "", data: toInputDate(now), motivo: "", datas: [] });
        setFormError("");
        setModalForm(true);
    };

    const abrirEditar = (falta) => {
        setEditando(falta);
        setForm({ funcionarioId: falta.funcionarioId, data: falta.data, motivo: falta.motivo || "", filialId: falta.funcionarioFilialId });
        setFormError("");
        setModalForm(true);
        setModalView(false);
    };

    const salvar = () => {
        if (!form.funcionarioId || (editando ? !form.data : !form.datas?.length)) {
            setFormError("Funcionário e data são obrigatórios.");
            return;
        }
        setFormLoading(true);
        setFormError("");

        const promise = editando
            ? faltaService.atualizar(editando.id, form)
            : faltaService.registrarPorFuncionario(form);

        promise
            .then(() => { setModalForm(false); carregar(); })
            .catch(err => setFormError(err?.response?.data?.message || "Erro ao salvar."))
            .finally(() => setFormLoading(false));
    };

    const remover = (id) => {
        faltaService.remover(id).then(() => { setModalView(false); carregar(); });
    };

    const abrirSincronizacaoFortes = () => {
        setFortesFilialId("");
        setFortesErro("");
        setFortesResultado(null);
        setModalFortes(true);
    };

    const sincronizarFortes = () => {
        setFortesLoading(true);
        setFortesErro("");
        setFortesResultado(null);

        faltaService.sincronizarFortes({ inicio, fim, filialId: fortesFilialId || null })
            .then(res => { setFortesResultado(res.data); carregar(); })
            .catch(err => setFortesErro(err?.response?.data?.message || "Erro ao sincronizar com o Fortes."))
            .finally(() => setFortesLoading(false));
    };

    // ── Table columns ─────────────────────────────────────────────────────────

    const columns = useMemo(() => [
        {
            key: "data",
            label: "Data",
            sortable: true,
            render: (v) => (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-muted border border-border px-2.5 py-0.5 text-[10px] font-bold text-foreground tabular-nums">
                    <i className="fa fa-calendar-day text-muted-foreground text-[9px]" />
                    {fmtDate(v)}
                </span>
            ),
        },
        {
            key: "funcionarioNome",
            label: "Funcionário",
            sortable: true,
            render: (v, row) => (
                <div>
                    <p className="font-semibold text-foreground text-[11px]">{v}</p>
                    <p className="text-[10px] text-muted-foreground">{row.funcionarioCodigo} · {row.funcionarioCargo}</p>
                </div>
            ),
        },
        {
            key: "filialNome",
            label: "Filial",
            sortable: true,
        },
        {
            key: "quantidadeDias",
            label: "Dias",
            sortable: true,
            center: true,
            render: (v) => <span className="font-bold tabular-nums">{v || 1}</span>,
        },
        {
            key: "origem",
            label: "Origem",
            sortable: true,
            render: (v) => v === "FORTES"
                ? <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                    <i className="fa fa-cloud-download-alt text-[9px]" /> Fortes
                </span>
                : <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                    <i className="fa fa-user-edit text-[9px]" /> Manual
                </span>,
        },
        {
            key: "motivo",
            label: "Motivo",
            render: (v) => v
                ? <span className="text-foreground">{v}</span>
                : <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-orange-600 bg-orange-500/10 border border-orange-500/20 rounded-full px-2 py-0.5">
                    <i className="fa fa-exclamation-circle text-[9px]" />
                    Sem motivo
                </span>,
        },
    ], []);

    const tableHeader = (
        <div className="flex items-center justify-between gap-2 px-1 py-1">
            <div className="relative">
                <i className="fa fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-[11px]" />
                <input
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                    placeholder="Buscar funcionário, cargo, motivo..."
                    className="pl-7 pr-7 py-1.5 rounded-lg border border-input bg-muted text-xs transition-all duration-150 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary w-72"
                />
                {busca && (
                    <button
                        onClick={() => setBusca("")}
                        title="Limpar busca"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-[10px]"
                    >
                        <i className="fa fa-times" />
                    </button>
                )}
            </div>
            <button
                onClick={abrirSincronizacaoFortes}
                className="flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/10 hover:bg-primary/15 text-primary text-xs font-semibold px-3 py-1.5 transition-all duration-150 active:translate-y-px shadow-xs"
            >
                <i className="fa fa-sync text-[10px]" />
                Sincronizar Fortes
            </button>
            <button
                onClick={() => setModalImport(true)}
                className="flex items-center gap-1.5 rounded-lg border border-border bg-secondary hover:bg-muted text-secondary-foreground text-xs font-semibold px-3 py-1.5 transition-all duration-150 active:translate-y-px shadow-xs"
            >
                <i className="fa fa-upload text-[10px]" />
                Importar Planilha
            </button>
            <button
                onClick={abrirCriar}
                className="flex items-center gap-1.5 rounded-lg bg-primary hover:brightness-95 text-primary-foreground text-xs font-semibold px-3 py-1.5 transition-all duration-150 active:translate-y-px shadow-xs"
            >
                <i className="fa fa-plus text-[10px]" />
                Registrar falta
            </button>
        </div>
    );

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="bg-background px-4 py-2">
            <div className="max-w-[1600px] mx-auto space-y-6">

                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex flex-wrap items-end justify-between gap-4"
                >
                    <div className="flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-destructive/10 text-destructive ring-1 ring-destructive/20">
                            <i className="fa fa-user-clock text-lg" />
                        </div>
                        <div>
                            <h1 className="font-display text-2xl font-bold text-foreground tracking-tight">Faltas</h1>
                            <p className="mt-0.5 text-sm text-muted-foreground">Controle de ausências de funcionários</p>
                        </div>
                    </div>

                    {/* Period filter */}
                    <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 shadow-card transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
                        <i className="fa fa-calendar-alt text-primary text-sm" />
                        <span className="text-xs text-muted-foreground font-medium">De</span>
                        <input
                            type="date"
                            value={inicio}
                            onChange={e => setInicio(e.target.value)}
                            className="border-0 bg-transparent text-sm font-medium text-foreground focus:outline-none cursor-pointer"
                        />
                        <span className="text-border">—</span>
                        <span className="text-xs text-muted-foreground font-medium">Até</span>
                        <input
                            type="date"
                            value={fim}
                            onChange={e => setFim(e.target.value)}
                            className="border-0 bg-transparent text-sm font-medium text-foreground focus:outline-none cursor-pointer"
                        />
                    </div>
                </motion.div>

                {/* Stat cards */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <StatCard
                        label="Total de faltas"
                        value={stats.total}
                        icon="fa fa-calendar-times"
                        colorCls="bg-destructive/5 border-destructive/20 text-destructive"
                        delay={0.05}
                    />
                    <StatCard
                        label="Funcionários"
                        value={stats.funcionarios}
                        icon="fa fa-users"
                        colorCls="bg-primary/5 border-primary/20 text-primary"
                        delay={0.10}
                    />
                    <StatCard
                        label="Sem motivo"
                        value={stats.semMotivo}
                        icon="fa fa-exclamation-circle"
                        colorCls="bg-orange-500/5 border-orange-500/20 text-orange-600"
                        delay={0.15}
                    />
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.2 }}
                        className="group relative flex items-center gap-4 overflow-hidden rounded-xl border border-amber-500/20 bg-amber-500/5 px-5 py-4 shadow-card text-foreground transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
                    >
                        <div className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full bg-amber-500 opacity-[0.06] transition-transform duration-300 group-hover:scale-125" />
                        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-card/70 text-lg text-amber-600 shadow-xs">
                            <i className="fa fa-trophy" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[10px] font-semibold uppercase tracking-widest opacity-60">Mais ausente</p>
                            {stats.top ? (
                                <>
                                    <p className="text-sm font-bold truncate">{stats.top[0]}</p>
                                    <p className="text-[10px] opacity-60">{stats.top[1]} falta{stats.top[1] > 1 ? "s" : ""}</p>
                                </>
                            ) : (
                                <p className="text-sm font-bold">—</p>
                            )}
                        </div>
                    </motion.div>
                </div>

                {/* Table */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: 0.25 }}
                >
                    <CustomTable
                        data={filtered}
                        loading={loading}
                        header={tableHeader}
                        columns={columns}
                        maxHeight="calc(100vh - 335px)"
                        className="text-[11px]"
                        onRowSelect={(row) => { setSelected(row); setModalView(true); }}
                    />
                </motion.div>
            </div>

            <Dialog
                header={
                    <div className="flex items-center gap-2 text-base font-bold text-foreground">
                        <i className="fa fa-calendar-times text-destructive" />
                        Importação de Faltas
                    </div>
                }
                visible={modalImport}
                modal
                className="w-[480px]"
                onHide={() => setModalImport(false)}
                pt={{ content: { className: "pt-2 pb-4 px-5" } }}
            >
                <FaltasImport filiais={filiais} onSuccess={() => { carregar(); setModalImport(false) }} />
            </Dialog>

            {/* ── Sincronização com o Fortes ── */}
            <Dialog
                header={
                    <div className="flex items-center gap-2 text-base font-bold text-foreground">
                        <i className="fa fa-sync text-primary" />
                        Sincronizar faltas com o Fortes
                    </div>
                }
                visible={modalFortes}
                modal
                className="w-[560px]"
                onHide={() => !fortesLoading && setModalFortes(false)}
                pt={{ content: { className: "pt-2 pb-4 px-5" } }}
            >
                <div className="space-y-3">
                    <div className="rounded-lg border border-border bg-muted px-4 py-3 text-xs text-muted-foreground space-y-1.5">
                        <p>
                            <i className="fa fa-calendar-alt mr-1.5 text-primary" />
                            Período: <strong className="text-foreground">{fmtDate(inicio)} a {fmtDate(fim)}</strong>
                            <span className="ml-1">(use os campos de data da página)</span>
                        </p>
                        <p>
                            <i className="fa fa-exclamation-triangle mr-1.5 text-orange-500" />
                            Antes de importar, as faltas de origem <strong className="text-foreground">Fortes</strong> do
                            período serão apagadas e regravadas. As lançadas manualmente ou por planilha não são alteradas.
                        </p>
                        <p>
                            <i className="fa fa-info-circle mr-1.5 text-primary" />
                            O Fortes informa só a quantidade de dias do mês: entra uma falta por funcionário, datada no
                            primeiro dia do mês da folha. Informe o período a partir do dia 1º (ex.: 01/09 a 30/09).
                        </p>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-foreground/80">Filial</label>
                        <select
                            value={fortesFilialId}
                            onChange={e => setFortesFilialId(e.target.value)}
                            disabled={fortesLoading}
                            className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground outline-none"
                        >
                            <option value="">Todas as filiais com Fortes configurado</option>
                            {filiais
                                .filter(f => f.codigoEmpresa && f.codigoFortes)
                                .map(f => (
                                    <option key={f.id} value={f.id}>
                                        {f.nome} (empresa {f.codigoEmpresa} / estab. {f.codigoFortes})
                                    </option>
                                ))}
                        </select>
                    </div>

                    {fortesErro && (
                        <p className="rounded-lg bg-destructive/5 border border-destructive/20 text-destructive text-xs px-3 py-2">
                            <i className="fa fa-exclamation-triangle mr-1.5" />
                            {fortesErro}
                        </p>
                    )}

                    {fortesResultado && (
                        <div className="rounded-lg border border-success/30 bg-success/5 px-4 py-3 text-xs space-y-1.5">
                            <p className="font-semibold text-success">
                                <i className="fa fa-check-circle mr-1.5" />
                                Sincronização concluída
                            </p>
                            <p className="text-foreground">
                                {fortesResultado.faltasImportadas} funcionário(s) com falta ({fortesResultado.totalDias} dia(s)) importado(s);
                                {" "}{fortesResultado.faltasRemovidas} registro(s) anterior(es) do Fortes removido(s).
                            </p>
                            <p className="text-muted-foreground">Filiais: {fortesResultado.filiais?.join(", ")}</p>
                            {fortesResultado.avisos?.length > 0 && (
                                <ul className="list-disc pl-4 text-orange-600">
                                    {fortesResultado.avisos.map((a, i) => <li key={i}>{a}</li>)}
                                </ul>
                            )}
                            {fortesResultado.naoEncontrados?.length > 0 && (
                                <div>
                                    <p className="font-semibold text-orange-600">
                                        {fortesResultado.naoEncontrados.length} da folha não encontrado(s) no cadastro (não importados):
                                    </p>
                                    <ul className="mt-1 max-h-32 list-disc overflow-auto pl-4 text-muted-foreground">
                                        {fortesResultado.naoEncontrados.map((n, i) => <li key={i}>{n}</li>)}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex gap-2 justify-end border-t border-border pt-3">
                        <Button
                            label={fortesResultado ? "Fechar" : "Cancelar"}
                            variant="gray"
                            iconLeft="fa fa-arrow-left"
                            disabled={fortesLoading}
                            onClick={() => setModalFortes(false)}
                        />
                        {!fortesResultado && (
                            <Button
                                label={fortesLoading ? "Sincronizando..." : "Sincronizar"}
                                variant="blue"
                                iconLeft={fortesLoading ? "fa fa-spinner fa-spin" : "fa fa-sync"}
                                disabled={fortesLoading}
                                onClick={sincronizarFortes}
                            />
                        )}
                    </div>
                </div>
            </Dialog>

            {/* ── Detail Modal ── */}
            <Dialog
                header={
                    <div className="flex items-center gap-2 text-base font-bold text-foreground">
                        <i className="fa fa-calendar-times text-destructive" />
                        Detalhes da Falta
                    </div>
                }
                visible={modalView}
                modal
                className="w-[480px]"
                onHide={() => setModalView(false)}
                pt={{ content: { className: "pt-2 pb-4 px-5" } }}
            >
                <AnimatePresence>
                    {selected && (
                        <motion.div
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="space-y-3"
                        >
                            <div className="flex items-center gap-3 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-destructive/10 text-sm font-bold text-destructive">
                                    {selected.funcionarioNome?.split(" ").filter(Boolean).slice(0, 2).map(p => p[0]).join("").toUpperCase() || "?"}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-bold text-foreground">{selected.funcionarioNome}</p>
                                    <p className="truncate text-[11px] text-muted-foreground">{selected.funcionarioCargo || "—"}</p>
                                </div>
                                <span className="inline-flex items-center gap-1.5 rounded-full border border-destructive/20 bg-card px-2.5 py-1 text-[11px] font-bold text-destructive tabular-nums">
                                    <i className="fa fa-calendar-day text-[10px]" />
                                    {fmtDate(selected.data)}
                                </span>
                            </div>

                            <div className="rounded-lg bg-muted border border-border px-4 py-1.5">
                                <InfoRow label="Funcionário" value={selected.funcionarioNome} />
                                <InfoRow label="Código" value={selected.funcionarioCodigo} />
                                <InfoRow label="Cargo" value={selected.funcionarioCargo} />
                                <InfoRow label="Filial" value={selected.filialNome} />
                                <InfoRow label="Data" value={fmtDate(selected.data)} />
                                <InfoRow label="Dias" value={selected.quantidadeDias || 1} />
                                <InfoRow label="Origem" value={selected.origem === "FORTES" ? "Folha do Fortes" : "Lançamento manual"} />
                                <InfoRow label="Motivo" value={selected.motivo} />
                            </div>

                            {selected.origem === "FORTES" ? (
                                <p className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
                                    <i className="fa fa-info-circle mr-1.5 text-primary" />
                                    Falta importada da folha do Fortes (quantidade do mês, datada no primeiro dia do mês). Ela é
                                    regravada a cada sincronização; para corrigir, ajuste no Fortes e sincronize novamente.
                                </p>
                            ) : (
                                <div className="flex gap-2 justify-end border-t border-border pt-3">
                                    <Button
                                        label="Editar"
                                        variant="blue"
                                        iconLeft="fa fa-pencil"
                                        onClick={() => abrirEditar(selected)}
                                    />
                                    <Button
                                        label="Excluir"
                                        variant="red"
                                        iconLeft="fa fa-trash"
                                        onClick={() => remover(selected.id)}
                                    />
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </Dialog>

            {/* ── Form Modal ── */}
            <Dialog
                blockScroll
                header={
                    <div className="flex items-center gap-2 text-base font-bold text-foreground">
                        <i className={`fa ${editando ? "fa-pencil text-primary" : "fa-plus-circle text-success"}`} />
                        {editando ? "Editar Falta" : "Registrar Falta"}
                    </div>
                }
                visible={modalForm}
                modal
                className="min-w-[70%]"
                onHide={() => setModalForm(false)}
                pt={{ content: { className: "pt-2 pb-4 px-5" } }}
            >
                <div className="grid grid-cols-2 gap-4">

                    {!editando && (
                        <DropDown
                            label="Filial"
                            options={filiais}
                            value={form.filialId}
                            onChange={e => setForm(f => ({ ...f, filialId: e.target.value, funcionarioId: "" }))}
                            optionLabel="nome"
                            optionValue="id"
                            filter={false}
                        />
                    )}

                    {!editando && (
                        <DropDown
                            label="Funcionário"
                            options={funcionarios}
                            value={form.funcionarioId}
                            onChange={e => setForm(f => ({ ...f, funcionarioId: e.target.value }))}
                            optionLabel="nome"
                            optionValue="id"
                            disabled={!form.filialId}
                        />
                    )}

                    <div className="col-span-2">
                        {!editando ? (
                            <>
                                <MultiDatePicker
                                    label="Datas das faltas"
                                    value={form.datas}
                                    onChange={e => setForm(f => ({ ...f, datas: e || [] }))}
                                />
                                {form.datas?.length > 0 && (
                                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mr-1">
                                            {form.datas.length} dia{form.datas.length > 1 ? "s" : ""} selecionado{form.datas.length > 1 ? "s" : ""}
                                        </span>
                                        {[...form.datas]
                                            .sort((a, b) => a - b)
                                            .map(d => (
                                                <span
                                                    key={d.getTime()}
                                                    className="inline-flex items-center gap-1 rounded-full border border-destructive/20 bg-destructive/5 px-2 py-0.5 text-[10px] font-semibold text-destructive tabular-nums"
                                                >
                                                    <i className="fa fa-calendar-day text-[9px] opacity-70" />
                                                    {d.toLocaleDateString("pt-BR")}
                                                </span>
                                            ))}
                                    </div>
                                )}
                            </>
                        ) : (
                            <DateMasked
                                label="Data da Falta"
                                value={form.data}
                                disabled={!!editando || !form.filialId}
                                onChange={e => setForm(f => ({ ...f, data: e }))}
                            />
                        )}
                    </div>

                    <div className="col-span-2">
                        <Textarea
                            label="Motivo"
                            value={form.motivo}
                            onChange={e => setForm(f => ({ ...f, motivo: e.target.value }))}
                            disabled={!form.filialId}
                        />
                    </div>

                    {formError && (
                        <p className="col-span-2 rounded-lg bg-destructive/5 border border-destructive/20 text-destructive text-xs px-3 py-2">
                            <i className="fa fa-exclamation-triangle mr-1.5" />
                            {formError}
                        </p>
                    )}

                    <div className="col-span-2 flex gap-2 justify-end border-t border-border pt-4">
                        <Button
                            label="Cancelar"
                            variant="gray"
                            iconLeft="fa fa-arrow-left"
                            onClick={() => setModalForm(false)}
                        />
                        <Button
                            label={formLoading ? "Salvando..." : "Salvar"}
                            variant="blue"
                            iconLeft="fa fa-save"
                            disabled={formLoading}
                            onClick={salvar}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
